import { useEffect, useState } from 'react';
import {
  IconReportMoney, IconReceipt2, IconUsers, IconCash, IconAlertCircle,
  IconArrowUpRight, IconArrowDownRight, IconPackages,
} from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';

/**
 * AdminDashboardPage — Phase 4.1 landing.
 *
 * Pulls /api/day-sessions/x-report (mid-day "read") for today's open
 * session and renders the headline KPIs. When no session is open,
 * shows a clean empty-state.
 *
 * Cohesion: every number comes from the same `computeDayReport`
 * helper the X/Z reports use. No duplicate aggregation.
 */

interface ReportShape {
  session: {
    id: string;
    openedAt: string;
    closedAt: string | null;
    openingFloatCents: number;
    openedBy: { name: string } | null;
  };
  totals: {
    grossSalesCents: number;
    netSalesCents: number;
    tipCents: number;
    vatCents: number;
    discountCents: number;
    refundedCents: number;
    cancelledCents: number;
    voidedCents: number;
    counts: Record<string, number>;
  };
  paymentsByMethod: Array<{ method: string; count: number; amountCents: number; netCents: number }>;
  cash: { expectedCashCents: number };
}

function fmtR(cents: number) {
  return `R ${(cents / 100).toFixed(2)}`;
}

function methodLabel(m: string) {
  return m === 'CARD_YOCO' ? 'Card' :
         m === 'CASH' ? 'Cash' :
         m === 'EFT' ? 'EFT' :
         m === 'ACCOUNT' ? 'Account' :
         m === 'VOUCHER' ? 'Voucher' :
         m === 'GIFT_CARD' ? 'Gift card' :
         'Other';
}

export default function AdminDashboardPage() {
  const [report, setReport] = useState<ReportShape | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lowStock, setLowStock] = useState<{ productId: string; name: string; onHand: number; reorderPoint: number }[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('/api/day-sessions/x-report', { credentials: 'include' });
        if (cancelled) return;
        if (r.status === 404) {
          setReport(null);
          return;
        }
        if (!r.ok) {
          const data = await r.json().catch(() => ({}));
          throw new Error(data.error ?? `HTTP ${r.status}`);
        }
        const data = await r.json();
        setReport(data.report);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    // Low stock — independent fetch, fail-safe
    (async () => {
      try {
        const r = await fetch('/api/stock', { credentials: 'include' });
        if (!r.ok || cancelled) return;
        const data = await r.json();
        const low = (data.products ?? []).filter((p: { flagged: boolean }) => p.flagged);
        setLowStock(low.slice(0, 5));
      } catch { /* swallow */ }
    })();

    return () => { cancelled = true; };
  }, []);

  const totalOrders = report
    ? Object.entries(report.totals.counts).reduce((s, [k, n]) => s + (k === 'CANCELLED' ? 0 : n), 0)
    : 0;
  const avgTicketCents = totalOrders > 0 ? Math.round((report?.totals.netSalesCents ?? 0) / totalOrders) : 0;

  return (
    <AdminLayout title="Dashboard" subtitle="Today, at a glance">
      {loading && <p className="text-13 font-semibold text-gray-700">Loading…</p>}

      {error && (
        <div className="rounded-md bg-danger-soft border border-danger/30 p-3 flex items-start gap-2 mb-5">
          <IconAlertCircle size={18} className="text-danger flex-shrink-0 mt-0.5" />
          <p className="text-13 font-semibold text-danger">{error}</p>
        </div>
      )}

      {!loading && !report && (
        <div className="rounded-xl bg-surface border-2 border-dashed border-border p-10 text-center">
          <div className="w-14 h-14 rounded-2xl bg-primary-soft text-primary inline-flex items-center justify-center mb-4 shadow-sm">
            <IconReportMoney size={26} stroke={2} />
          </div>
          <h2 className="text-18 font-bold text-heading mb-1.5">No day session open</h2>
          <p className="text-14 font-medium text-gray-700 max-w-md mx-auto leading-relaxed">
            The dashboard reads from today's open day session. Ask a supervisor to open the day at the till, then come back here for live numbers.
          </p>
          <Link
            to="/pos"
            className="inline-flex items-center gap-2 mt-5 h-11 px-5 rounded-md bg-primary text-[#FFFFFF] text-14 font-bold hover:bg-primary-hover shadow-sm"
          >
            Open the till
          </Link>
        </div>
      )}

      {report && (
        <>
          {/* KPI tiles */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <div className="rounded-lg bg-surface border border-border p-4">
              <p className="text-12 font-bold uppercase tracking-wider text-gray-700">Net sales</p>
              <p className="text-26 font-bold text-heading tabular-nums mt-1">{fmtR(report.totals.netSalesCents)}</p>
              <p className="text-12 font-medium text-gray-700 mt-0.5">Gross {fmtR(report.totals.grossSalesCents)}</p>
            </div>
            <div className="rounded-lg bg-surface border border-border p-4">
              <p className="text-12 font-bold uppercase tracking-wider text-gray-700">Orders</p>
              <p className="text-26 font-bold text-heading tabular-nums mt-1">{totalOrders}</p>
              <p className="text-12 font-medium text-gray-700 mt-0.5">Avg ticket {fmtR(avgTicketCents)}</p>
            </div>
            <div className="rounded-lg bg-surface border border-border p-4">
              <p className="text-12 font-bold uppercase tracking-wider text-gray-700">Expected cash</p>
              <p className="text-26 font-bold text-primary tabular-nums mt-1">{fmtR(report.cash.expectedCashCents)}</p>
              <p className="text-12 font-medium text-gray-700 mt-0.5">In the drawer right now</p>
            </div>
            <div className="rounded-lg bg-surface border border-border p-4">
              <p className="text-12 font-bold uppercase tracking-wider text-gray-700">VAT collected</p>
              <p className="text-26 font-bold text-heading tabular-nums mt-1">{fmtR(report.totals.vatCents)}</p>
              <p className="text-12 font-medium text-gray-700 mt-0.5">Included in totals</p>
            </div>
          </div>

          {/* Two-up: payment-by-method + tip/discount/refund */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <section className="rounded-lg bg-surface border border-border p-5">
              <h2 className="text-15 font-bold text-heading mb-3">By payment method</h2>
              {report.paymentsByMethod.length === 0 ? (
                <p className="text-13 italic text-gray-700">No payments yet today.</p>
              ) : (
                <ul className="space-y-2">
                  {report.paymentsByMethod.map((p) => (
                    <li key={p.method} className="flex items-center justify-between p-3 rounded-md bg-light border border-border-soft">
                      <div>
                        <p className="text-14 font-bold text-heading">{methodLabel(p.method)}</p>
                        <p className="text-12 font-medium text-gray-700">{p.count} {p.count === 1 ? 'payment' : 'payments'}</p>
                      </div>
                      <p className="text-16 font-bold text-heading tabular-nums">{fmtR(p.netCents)}</p>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="rounded-lg bg-surface border border-border p-5">
              <h2 className="text-15 font-bold text-heading mb-3">Adjustments</h2>
              <ul className="space-y-2">
                <li className="flex items-center justify-between p-3 rounded-md bg-light border border-border-soft">
                  <span className="inline-flex items-center gap-2 text-14 font-bold text-heading">
                    <IconArrowUpRight size={14} className="text-success" />
                    Tips
                  </span>
                  <span className="text-15 font-bold text-heading tabular-nums">{fmtR(report.totals.tipCents)}</span>
                </li>
                <li className="flex items-center justify-between p-3 rounded-md bg-light border border-border-soft">
                  <span className="inline-flex items-center gap-2 text-14 font-bold text-heading">
                    <IconArrowDownRight size={14} className="text-warning" />
                    Discounts given
                  </span>
                  <span className="text-15 font-bold text-heading tabular-nums">{fmtR(report.totals.discountCents)}</span>
                </li>
                <li className="flex items-center justify-between p-3 rounded-md bg-light border border-border-soft">
                  <span className="inline-flex items-center gap-2 text-14 font-bold text-heading">
                    <IconArrowDownRight size={14} className="text-danger" />
                    Refunded
                  </span>
                  <span className="text-15 font-bold text-heading tabular-nums">{fmtR(report.totals.refundedCents)}</span>
                </li>
                <li className="flex items-center justify-between p-3 rounded-md bg-light border border-border-soft">
                  <span className="inline-flex items-center gap-2 text-14 font-bold text-heading">
                    <IconReceipt2 size={14} className="text-gray-700" />
                    Voided
                  </span>
                  <span className="text-15 font-bold text-heading tabular-nums">{fmtR(report.totals.voidedCents)}</span>
                </li>
              </ul>
            </section>
          </div>

          {/* Day session card */}
          <section className="rounded-lg bg-surface border border-border p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-md bg-success-soft text-success inline-flex items-center justify-center flex-shrink-0">
              <IconCash size={22} stroke={2} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-12 font-bold uppercase tracking-wider text-success">Day session — open</p>
              <p className="text-15 font-bold text-heading mt-0.5">
                Opened {new Date(report.session.openedAt).toLocaleString('en-ZA')}
                {report.session.openedBy?.name ? <> · by {report.session.openedBy.name}</> : null}
              </p>
              <p className="text-12 font-medium text-gray-700">Opening float {fmtR(report.session.openingFloatCents)}</p>
            </div>
            <Link
              to="/admin/days"
              className="inline-flex items-center h-10 px-4 rounded-md bg-light border border-border text-13 font-bold text-heading hover:border-primary hover:text-primary"
            >
              History →
            </Link>
          </section>

          {/* Low stock card */}
          {lowStock.length > 0 && (
            <section className="rounded-lg bg-surface border border-border p-5 mt-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-15 font-bold text-heading inline-flex items-center gap-2">
                  <IconPackages size={16} className="text-danger" />
                  Low stock ({lowStock.length})
                </h2>
                <Link to="/admin/stock" className="text-12 font-bold text-primary hover:underline">View all →</Link>
              </div>
              <ul className="space-y-2">
                {lowStock.map((p) => (
                  <li key={p.productId} className="flex items-center justify-between p-3 rounded-md bg-danger-soft border border-danger/30">
                    <div>
                      <p className="text-14 font-bold text-heading">{p.name}</p>
                      <p className="text-12 font-medium text-gray-700">Reorder point {p.reorderPoint}</p>
                    </div>
                    <p className="text-18 font-bold text-danger tabular-nums">{p.onHand}</p>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Quick-links */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
            {[
              { label: 'Orders', to: '/admin/orders', Icon: IconReceipt2 },
              { label: 'Products', to: '/admin/products', Icon: IconReportMoney },
              { label: 'Customers', to: '/admin/customers', Icon: IconUsers },
              { label: 'Day sessions', to: '/admin/days', Icon: IconCash },
            ].map((q) => (
              <Link
                key={q.to}
                to={q.to}
                className="rounded-lg bg-surface border-2 border-border hover:border-primary p-4 transition-colors flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-md bg-primary-soft text-primary inline-flex items-center justify-center">
                  <q.Icon size={20} stroke={2} />
                </div>
                <span className="text-14 font-bold text-heading">{q.label}</span>
              </Link>
            ))}
          </div>
        </>
      )}
    </AdminLayout>
  );
}
